import { useState, useMemo, useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import * as intelligenceApi from '../api';
import type { DealFilters } from '../api';
import type {
  PeriodPreset,
  DateRange,
  DealSummary,
  DealSummaryWithComparison,
  DealSummaryChanges,
} from '../types';
import {
  getPeriodDateRange,
  getPreviousPeriodRange,
  formatDateForApi,
  calculatePercentageChange,
  calculatePointsChange,
} from '../utils/periodUtils';

export interface PeriodFilterState {
  preset: PeriodPreset;
  customRange: DateRange | undefined;
  comparisonEnabled: boolean;
}

export interface UsePeriodFilterReturn {
  // State
  preset: PeriodPreset;
  customRange: DateRange | undefined;
  comparisonEnabled: boolean;
  // Computed ranges
  currentRange: DateRange;
  previousRange: DateRange;
  // API filters
  currentFilters: { dateFrom: string; dateTo: string };
  previousFilters: { dateFrom: string; dateTo: string };
  // Actions
  setPreset: (preset: PeriodPreset) => void;
  setCustomRange: (range: DateRange | undefined) => void;
  setComparisonEnabled: (enabled: boolean) => void;
}

/**
 * Hook to manage period filtering state
 * Provides current and previous period ranges for comparison
 */
export function usePeriodFilter(
  defaultPreset: PeriodPreset = 'this_month'
): UsePeriodFilterReturn {
  const [preset, setPreset] = useState<PeriodPreset>(defaultPreset);
  const [customRange, setCustomRange] = useState<DateRange | undefined>(undefined);
  const [comparisonEnabled, setComparisonEnabled] = useState(false);

  // Calculate current range based on preset
  const currentRange = useMemo(
    () => getPeriodDateRange(preset, customRange),
    [preset, customRange]
  );

  // Calculate previous range for comparison
  const previousRange = useMemo(
    () => getPreviousPeriodRange(currentRange, preset),
    [currentRange, preset]
  );

  // Format for API calls
  const currentFilters = useMemo(
    () => ({
      dateFrom: formatDateForApi(currentRange.from),
      dateTo: formatDateForApi(currentRange.to),
    }),
    [currentRange]
  );

  const previousFilters = useMemo(
    () => ({
      dateFrom: formatDateForApi(previousRange.from),
      dateTo: formatDateForApi(previousRange.to),
    }),
    [previousRange]
  );

  // Handle preset change - reset custom range when switching away from custom
  const handleSetPreset = useCallback((newPreset: PeriodPreset) => {
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      setCustomRange(undefined);
    }
  }, []);

  return {
    preset,
    customRange,
    comparisonEnabled,
    currentRange,
    previousRange,
    currentFilters,
    previousFilters,
    setPreset: handleSetPreset,
    setCustomRange,
    setComparisonEnabled,
  };
}

/**
 * Calculate changes between current and previous deal summaries
 */
function calculateDealSummaryChanges(
  current: DealSummary,
  previous: DealSummary
): DealSummaryChanges {
  return {
    totalPipelineValue: calculatePercentageChange(
      current.totalPipelineValue,
      previous.totalPipelineValue
    ),
    openDeals: calculatePercentageChange(current.openDeals, previous.openDeals),
    totalWonValue: calculatePercentageChange(
      current.totalWonValue,
      previous.totalWonValue
    ),
    // Win rate is a percentage, so we calculate absolute points change
    winRate: calculatePointsChange(current.winRate, previous.winRate),
    avgDealSize: calculatePercentageChange(
      current.avgDealSize,
      previous.avgDealSize
    ),
  };
}

/**
 * Hook to fetch deal summary with optional comparison to previous period
 */
export function useDealSummaryWithComparison(
  filters: DealFilters,
  comparisonEnabled: boolean,
  previousFilters: DealFilters
): {
  data: DealSummaryWithComparison | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
} {
  const queries = useQueries({
    queries: [
      {
        queryKey: ['intelligence', 'deals', 'summary', filters],
        queryFn: () => intelligenceApi.getDealSummary(filters),
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['intelligence', 'deals', 'summary', previousFilters],
        queryFn: () => intelligenceApi.getDealSummary(previousFilters),
        staleTime: 5 * 60 * 1000,
        enabled: comparisonEnabled, // Only fetch when comparison is enabled
      },
    ],
  });

  const [currentQuery, previousQuery] = queries;

  const isLoading = currentQuery.isLoading || (comparisonEnabled && previousQuery.isLoading);
  const isFetching = currentQuery.isFetching || (comparisonEnabled && previousQuery.isFetching);
  const error = currentQuery.error || (comparisonEnabled ? previousQuery.error : null);

  const data = useMemo(() => {
    if (!currentQuery.data) return undefined;

    const current = currentQuery.data;
    const previous = comparisonEnabled && previousQuery.data ? previousQuery.data : null;
    const changes = previous ? calculateDealSummaryChanges(current, previous) : null;

    return {
      current,
      previous,
      changes,
    };
  }, [currentQuery.data, previousQuery.data, comparisonEnabled]);

  return {
    data,
    isLoading,
    isFetching,
    error: error as Error | null,
  };
}
